// Edytor pojedynczej podstrony CMS

import React, { FC, useEffect, useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowLeft, FileEarmarkText, Link45deg, Globe } from 'react-bootstrap-icons';

import { useGetCmsArticle, usePutCmsArticle, usePostCmsArticle, useGetCmsArticleCountries, useGetCmsSectionFields } from 'api';
import { Button, Checkbox, Loader, TranslatedEditor } from 'components/controls';

import styles from 'theme/pages/PanelPages/PanelPageEditor.module.scss';

const SECTION_ID = 62;

interface IProps {
  articleId?: number;
  onBack: () => void;
  onSaved: () => void;
}

const EditPage: FC<IProps> = ({ articleId, onBack, onSaved }) => {
  const { t } = useTranslation();
  const isNew = !articleId;

  const [language, setLanguage] = useState('pl');

  const { data: countries } = useGetCmsArticleCountries();
  const { data: sectionFields } = useGetCmsSectionFields(SECTION_ID, { page: 1, limit: 100 }, { enabled: isNew });

  const { data: articleData, isLoading } = useGetCmsArticle(
    articleId || 0,
    undefined,
    { enabled: !!articleId, cacheTime: 0, staleTime: 0 }
  );

  const { mutateAsync: updateArticle, isLoading: isSaving } = usePutCmsArticle(articleId || 0);
  const { mutateAsync: createArticle, isLoading: isCreating } = usePostCmsArticle();

  // Stan formularza
  const [symbol, setSymbol] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [titles, setTitles] = useState<{ language: string; translation: string }[]>([]);
  const [htmlValues, setHtmlValues] = useState<{ language: string; translation: string }[]>([]);
  const [urlValues, setUrlValues] = useState<{ language: string; translation: string }[]>([]);

  // htmlFieldId i urlFieldId z article_fields
  const [htmlFieldId, setHtmlFieldId] = useState<number | null>(null);
  const [urlFieldId, setUrlFieldId] = useState<number | null>(null);

  // Inicjalizacja z danych API
  useEffect(() => {
    if (!articleData || !countries?.items) return;

    const langs = countries.items.map((c: any) => c.code);

    const fillMissingLangs = (
      arr: { language: string; translation: string }[]
    ): { language: string; translation: string }[] => {
      const existing = Array.isArray(arr) ? arr : [];
      return langs.map((lang: string) => {
        const found = existing.find((x) => x.language === lang);
        return found || { language: lang, translation: '' };
      });
    };

    setSymbol(articleData.symbol || '');
    setIsActive(articleData.is_active ?? true);
    setTitles(fillMissingLangs(articleData.title));

    const htmlField = articleData.article_fields?.find((f: any) => f.symbol === 'html');
    const urlField = articleData.article_fields?.find((f: any) => f.symbol === 'url');

    if (htmlField) {
      setHtmlFieldId(htmlField.section_field_id);
      setHtmlValues(fillMissingLangs(htmlField.value));
    }
    if (urlField) {
      setUrlFieldId(urlField.section_field_id);
      setUrlValues(fillMissingLangs(urlField.value));
    }
  }, [articleData, countries]);

  // Inicjalizacja pustych wartości dla języków przy nowym artykule
  useEffect(() => {
    if (!isNew || !countries?.items) return;
    const langs = countries.items.map((c: any) => c.code);
    setTitles(langs.map((lang: string) => ({ language: lang, translation: '' })));
    setHtmlValues(langs.map((lang: string) => ({ language: lang, translation: '' })));
    setUrlValues(langs.map((lang: string) => ({ language: lang, translation: '' })));
  }, [isNew, countries]);

  const getTranslation = (arr: { language: string; translation: string }[], lang: string) =>
    arr.find((x) => x.language === lang)?.translation || '';

  const setTranslation = useCallback(
    (
      arr: { language: string; translation: string }[],
      setArr: (v: { language: string; translation: string }[]) => void,
      lang: string,
      value: string
    ) => {
      setArr(arr.map((x) => (x.language === lang ? { ...x, translation: value } : x)));
    },
    []
  );

  const buildPayload = () => {
    const fileTemplate = (countries?.items || []).map((c: any) => ({
      language: c.code,
      url: null,
      is_active: null,
      name: null,
      description: null
    }));

    const resolvedHtmlFieldId = htmlFieldId ?? sectionFields?.items?.find((f: any) => f.symbol === 'html')?.id ?? null;
    const resolvedUrlFieldId = urlFieldId ?? sectionFields?.items?.find((f: any) => f.symbol === 'url')?.id ?? null;

    return {
      section_id: SECTION_ID,
      symbol,
      title: titles,
      language,
      from_date: articleData?.from_date || null,
      to_date: articleData?.to_date || null,
      is_active: isActive,
      layouts: articleData?.layouts || 'Desktop|Mobile',
      article_fields: [
        ...(resolvedHtmlFieldId
          ? [{
              section_field_id: resolvedHtmlFieldId,
              type: 'HTMLBLOCK',
              symbol: 'html',
              value: htmlValues,
              file: fileTemplate
            }]
          : []),
        ...(resolvedUrlFieldId
          ? [{
              section_field_id: resolvedUrlFieldId,
              type: 'STRING',
              symbol: 'url',
              value: urlValues,
              blob_value: [],
              file: fileTemplate
            }]
          : [])
      ]
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (isNew) {
      await createArticle(payload as any);
    } else {
      await updateArticle(payload as any);
    }
  };

  const currentTitle = getTranslation(titles, language);
  const currentUrl = getTranslation(urlValues, language);
  const currentHtml = getTranslation(htmlValues, language);

  const isInitialized = isNew ? titles.length > 0 : htmlValues.length > 0 || urlValues.length > 0;

  if (!isNew && (isLoading || !isInitialized)) {
    return <Loader />;
  }

  return (
    <div className={styles.wrapper}>

      {/* Page header */}
      <div className={styles.pageHeader}>
        <button className={styles.backBtn} onClick={onBack} title={t('Wróć')}>
          <ArrowLeft size={16} />
        </button>
        <div className={styles.headerMeta}>
          <h1 className={styles.pageTitle}>
            {isNew ? <Trans>Nowa podstrona</Trans> : (currentTitle || symbol || <Trans>Edytuj podstronę</Trans>)}
          </h1>
          {currentUrl && (
            <p className={styles.pageSubtitle}>/{currentUrl}</p>
          )}
        </div>

        {/* Przełącznik języka */}
        <div className={styles.langTabs}>
          {(countries?.items || []).map((c: any) => (
            <button
              key={c.code}
              className={`${styles.langTab} ${language === c.code ? styles.langTabActive : ''}`}
              onClick={() => setLanguage(c.code)}>
              {c.code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Karta: Podstawowe */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><FileEarmarkText /></span>
          <h2 className={styles.cardTitle}><Trans>Dane podstrony</Trans></h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.row}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}><Trans>Tytuł strony</Trans></label>
              <input
                className={styles.urlInput}
                style={{ border: '1px solid var(--color-form-border, #dbdee4)', borderRadius: 6, padding: '8px 12px', height: 36, fontSize: 14 }}
                value={currentTitle}
                placeholder={t('Np. O nas')}
                onChange={(e) => setTranslation(titles, setTitles, language, e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}><Trans>Symbol</Trans></label>
              <input
                className={styles.urlInput}
                style={{ border: '1px solid var(--color-form-border, #dbdee4)', borderRadius: 6, padding: '8px 12px', height: 36, fontSize: 13, fontFamily: 'monospace' }}
                value={symbol}
                placeholder="O_NAS"
                onChange={(e) => setSymbol(e.target.value.toUpperCase().replace(/\s/g, '_'))}
                disabled={!isNew}
              />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label className={styles.label}><Trans>Aktywna</Trans></label>
            <div className={styles.activeToggle} onClick={() => setIsActive(!isActive)}>
              <Checkbox checked={isActive} onClick={undefined} />
              <span className={styles.activeToggleLabel}>
                {isActive ? <Trans>Podstrona widoczna</Trans> : <Trans>Podstrona ukryta</Trans>}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Karta: URL */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><Link45deg /></span>
          <h2 className={styles.cardTitle}><Trans>Przyjazny URL</Trans></h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              <Trans>Adres podstrony</Trans> ({language.toUpperCase()})
            </label>
            <div className={styles.urlInputWrapper}>
              <span className={styles.urlPrefix}>/</span>
              <input
                className={styles.urlInput}
                value={currentUrl}
                placeholder={t('np. o-nas')}
                onChange={(e) =>
                  setTranslation(urlValues, setUrlValues, language, e.target.value.toLowerCase().replace(/\s/g, '-'))
                }
              />
            </div>
            <span style={{ fontSize: 11, color: 'var(--color-label)', marginTop: 4 }}>
              <Trans>Używaj myślników zamiast spacji, np.</Trans> <code>o-nas</code>
            </span>
          </div>
        </div>
      </div>

      {/* Karta: Treść HTML */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><Globe /></span>
          <h2 className={styles.cardTitle}>
            <Trans>Treść strony</Trans> - {language.toUpperCase()}
          </h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.editorWrapper}>
            <TranslatedEditor
              name="html"
              value={htmlValues}
              selectedCountry={language}
              setFieldValue={(_name: string, val: any) => setHtmlValues(val)}
              countries={countries?.items || []}
            />
          </div>
        </div>
      </div>

      {/* Sticky save bar */}
      <div className={styles.saveBar}>
        <span className={styles.saveBarHint}>
          <Trans>Pamiętaj aby zapisać zmiany dla każdego języka osobno</Trans>
        </span>
        <div className={styles.saveBarButtons}>
          <Button color="gray" onClick={onBack}>
            <Trans>Anuluj</Trans>
          </Button>
          <Button color="primary" loading={isSaving || isCreating} onClick={handleSave}>
            <Trans>Zapisz podstronę</Trans>
          </Button>
        </div>
      </div>

    </div>
  );
};

export default EditPage;
